import {game} from "js/main";
import {FlyingBubble} from "js/Elements/BubbleFlyingBehaviour";
import {makeImage} from "js/utils/makeImage";
import {Shooter} from "js/Elements/Shooter";
import {FlyingBubblesGroup} from "js/Elements/FlyingBubblesGroup";
import {Vec2} from "js/Elements/types";
import {GameGrid} from "js/Elements/GameGrid";
import {BubblesMatchBehaviours} from "js/Elements/BubblesMatchBehaviours";

export class PointerLayer extends Phaser.Group {
  private dotPointers = new Phaser.Group(game);

  private bubbleHighlights: Phaser.Group = new Phaser.Group(game);

  private arrowPointer: Phaser.Image;

  private grid: GameGrid;
  private shooter: Shooter;
  private flyingGroup: FlyingBubblesGroup;

  // velX, velY, bubbleType
  private lastCheck: [number, number, number] = [-1, -1, -1];

  constructor() {
    super(game);

    this.preWarm();
    this.addChild(this.dotPointers);
    this.addChild(this.bubbleHighlights);

    this.arrowPointer = makeImage(0, 0, 'arrow', [0.5, (178 - (59 * 0.5)) / 178], this)
    this.arrowPointer.visible = false;
  }

  private preWarm() {
    for (let i = 0; i < 100; i++) {
      const dot = makeImage(0, 0, '0_bubble_grey', 0.5, this.dotPointers);
      dot.scale.set(0.3);
      dot.visible = false;
    }

    for (let i = 0; i < 50; i++) {
      const highlight = makeImage(0, 0, 'bubble_highlight', 0.5, this.bubbleHighlights);
      highlight.visible = false;
    }
  }

  public setDependencies(grid: GameGrid, shooter: Shooter, flyingGroup: FlyingBubblesGroup) {
    this.grid = grid;
    this.shooter = shooter;
    this.flyingGroup = flyingGroup;
  }

  public updatePointer(flyingBubble: FlyingBubble | null) {
    if (flyingBubble === null) {
      this.dotPointers.visible = false;
      this.arrowPointer.visible = false;
      this.bubbleHighlights.visible = false;
      return;
    }

    this.arrowPointer.x = flyingBubble.x;
    this.arrowPointer.y = flyingBubble.y;
    this.arrowPointer.rotation = Phaser.Math.angleBetween(0, 0, flyingBubble.vel.x, flyingBubble.vel.y) + Math.PI * 0.5;
    this.arrowPointer.visible = game.arrowPointer;
    this.dotPointers.visible = !game.arrowPointer;
    this.bubbleHighlights.visible = !game.arrowPointer;

    if (!game.arrowPointer && this.shouldRunSimulation(flyingBubble)) {
      this.updateDotPointer(flyingBubble);
    }
  }

  public invalidate() {
    this.lastCheck[0] = -1;
    this.lastCheck[1] = -1;
    this.lastCheck[2] = -1;
  }

  private shouldRunSimulation(flyingBubble: FlyingBubble) {
    return (this.lastCheck[0] !== flyingBubble.vel.x ||
    this.lastCheck[1] !== flyingBubble.vel.y ||
    this.lastCheck[2] !== flyingBubble.bubbleType);
  }

  private updateDotPointer(flyingBubble: FlyingBubble) {
    if (!this.flyingGroup) return;

    const result = this.flyingGroup.simulate(flyingBubble);
    this.clearHighlights();

    const isBomb = game.config.bubbleNames[flyingBubble.bubbleType] === 'bomb';

    if (isBomb && result.collision) {
      const matching = BubblesMatchBehaviours.getMatchingBubbles(this.grid, result.collision, flyingBubble.bubbleType);
      this.highlightBubbles(matching);
    }


    this.dotPointers.children.forEach((child) => {
      child.visible = false;
    });

    result.positions.forEach((pos, index) => {
      if (index % 2 !== 0) return;
      const child = this.dotPointers.children[index / 2];
      child.x = pos.x;
      child.y = pos.y;
      child.visible = true;
    });

    this.lastCheck[0] = flyingBubble.vel.x;
    this.lastCheck[1] = flyingBubble.vel.y;
    this.lastCheck[2] = flyingBubble.bubbleType;
  }

  private clearHighlights() {
    this.bubbleHighlights.children.forEach((child) => child.visible = false);
  }

  private highlightBubbles(cells: Vec2[]) {
    cells.forEach((cell, index) => {
      const bubble = this.grid.getBubble(cell.x, cell.y);
      const highlight = this.bubbleHighlights.children[index];
      if (!bubble || !highlight) return;
      highlight.visible = true;
      highlight.x = bubble.x + this.grid.x;
      highlight.y = bubble.y + this.grid.y;
    });
  }

  public update() {
    if (!this.shooter || !this.flyingGroup) return;

    const bubble = this.shooter.getCurrentFlyingBubble();
    this.updatePointer(bubble);
  }
}
